// SCR-STAFF-400 — پذیرش و مراجعه (FLOW-04؛ US-BIL-001..005، 010..013، 020): سربرگ مراجعه‌کننده، پرداخت‌کننده (خود، شخص، سازمان؛ بیمه فاز 3)، اقلام
// با ردیف قابل گسترش لایه‌های قیمت و منبع هر لایه، افزودن خدمت (Ins)، مبلغ Range، تخفیف دستی با تأیید مدیر، انجام و لغو قلم، جمع‌ها، صدور صورتحساب
// (Alt+P: صدور اقلام صادرنشده و رفتن به صندوق /cashier/checkout/:visitId — AP-BIL-02، C-AGENT-549)، بستن (با بدهی مجاز) و بازگشایی تا ۷ روز، صف اصلاح صورتحساب.
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap, type BilTotalsView, type BilVisitLineView, type BilVisitView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ConfirmDialog, ErrorState, faDigits, JalaliDate, LoadingState, PageHeader, ReasonDialog } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router';
import { useApproval } from '../iam/useApproval';
import { PatientHeader } from '../patients/PatientHeader';
import { AddLineDialog, AmountDialog, DiscountDialog, EditLineDialog, PayerDialog, useVisitCache } from './VisitDialogs';
import { DiscountInputsDialog, LinePackageCell } from './VisitPackageInputs';
import { Amount, bilKeys, checkoutPath, InvoiceStatusChip, invoicePath, LineStatusChip, num, SCR_STAFF_400, usePayerLabel, VisitStatusChip } from './common';

type LineDialog = { kind: 'edit' | 'amount' | 'discount' | 'cancel'; line: BilVisitLineView };

function LayersRow({ line, colSpan }: { line: BilVisitLineView; colSpan: number }) {
  const { t } = useTranslation('bil');
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ py: 0, borderBottom: 0 }}>
        <Box sx={{ py: 1, ps: 6 }} data-testid="bil-line-layers">
          <Table size="small" aria-label={t('bil:layers.caption')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('bil:layers.layer')}</TableCell>
                <TableCell>{t('bil:layers.amount')}</TableCell>
                <TableCell>{t('bil:layers.source')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {line.layers.map((layer, index) => (
                <TableRow key={`${layer.layer}-${index}`} data-testid="bil-layer" data-layer={layer.layer}>
                  <TableCell>{t(`bil:layer.${layer.layer}`)}</TableCell>
                  <TableCell>
                    <Amount money={layer.amount} />
                  </TableCell>
                  <TableCell>
                    <bdi dir="ltr">{layer.sourceRef ?? '—'}</bdi>
                    {layer.approvalId ? ` · ${t('bil:layers.approved')}` : ''}
                  </TableCell>
                </TableRow>
              ))}
              {line.taxRateMissing ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Alert severity="warning">{t('bil:layers.tax_missing')}</Alert>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function TotalsPanel({ totals }: { totals: BilTotalsView }) {
  const { t } = useTranslation('bil');
  const rows: [string, keyof BilTotalsView, boolean?][] = [
    ['gross', 'gross'],
    ['insurance', 'insuranceShare'],
    ['discount', 'discount'],
    ['package', 'packageCredit'],
    ['tax', 'tax'],
    ['rounding', 'rounding'],
    ['payable', 'payable', true],
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-totals">
      <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {rows.map(([label, key, strong]) => (
          <Box key={key} data-testid={`bil-total-${label}`}>
            <Typography variant="caption" color="text.secondary" component="div">
              {t(`bil:totals.${label}`)}
            </Typography>
            <Typography variant={strong ? 'h6' : 'body1'} component="div">
              <Amount money={totals[key]} />
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

export function VisitPage() {
  const { t } = useTranslation('bil');
  const { id = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const navigate = useNavigate();
  const payerLabel = usePayerLabel();
  const update = useVisitCache(id);
  const issueKey = useIdempotencyKey();
  const actionKey = useIdempotencyKey();
  const { requireApproval, dialog: approvalDialog } = useApproval();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [lineDialog, setLineDialog] = useState<LineDialog | null>(null);
  const [invoiceDiscount, setInvoiceDiscount] = useState(false);
  const [discountInputs, setDiscountInputs] = useState(false);
  const [payerOpen, setPayerOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const query = useQuery({
    queryKey: bilKeys.visit(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/visits/{id}', { params: { path: { id } } })),
    enabled: id !== '',
  });
  const visit = query.data;

  const full = access.accessMode === 'Full';
  const can = {
    manage: full && access.can('bil.visit.manage') && (visit?.actions.canEdit ?? false),
    discount: full && access.can('bil.discount.manual') && (visit?.actions.canEdit ?? false),
    issue: full && access.can('bil.invoice.issue') && (visit?.actions.canIssue ?? false),
    close: full && access.can('bil.visit.close') && (visit?.actions.canClose ?? false),
    reopen: full && access.can('bil.visit.reopen') && (visit?.actions.canReopen ?? false),
    amount: full && access.can('cat.price.override'),
    receive: full && access.can('bil.payment.receive'),
  };

  const toCashier = useRef(false);
  const issue = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/visits/{id}/invoices', {
          params: { path: { id } },
          headers: { 'Idempotency-Key': issueKey.current() },
        }),
      ),
    onSettled: (_data, error) => issueKey.settle(error ?? undefined),
    onSuccess: (issued) => {
      void query.refetch();
      setToast(t('bil:issue.done', { numbers: issued.invoices.map((invoice) => invoice.number).join(' · ') }));
      if (toCashier.current) {
        toCashier.current = false;
        void navigate(checkoutPath(id));
      }
    },
    onError: () => {
      toCashier.current = false;
    },
  });

  const lineAction = useMutation({
    mutationFn: ({ line, action, reason }: { line: BilVisitLineView; action: 'render' | 'cancel'; reason?: string }) =>
      action === 'render'
        ? unwrap(
            tenantApi.POST('/api/v1/billing/visits/{id}/lines/{lineId}:render', {
              params: { path: { id, lineId: line.id } },
              headers: { 'Idempotency-Key': actionKey.current() },
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/billing/visits/{id}/lines/{lineId}:cancel', {
              params: { path: { id, lineId: line.id } },
              body: { reason: reason ?? null },
              headers: { 'Idempotency-Key': actionKey.current() },
            }),
          ),
    onSettled: (_data, error) => actionKey.settle(error ?? undefined),
    onSuccess: (view) => {
      update(view);
      setLineDialog(null);
    },
  });

  const removeDiscount = useMutation({
    mutationFn: (line: BilVisitLineView | null) =>
      line
        ? unwrap(tenantApi.DELETE('/api/v1/billing/visits/{id}/lines/{lineId}/manual-discount', { params: { path: { id, lineId: line.id } } }))
        : unwrap(tenantApi.DELETE('/api/v1/billing/visits/{id}/manual-discount', { params: { path: { id } } })),
    onSuccess: update,
  });

  const applySuggested = useMutation({
    mutationFn: (view: BilVisitView) =>
      unwrap(tenantApi.PUT('/api/v1/billing/visits/{id}/payer', { params: { path: { id } }, body: view.suggestedPayer! })),
    onSuccess: update,
  });

  const resolve = useMutation({
    mutationFn: (correctionId: string) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/corrections/{id}:resolve', {
          params: { path: { id: correctionId } },
          body: { note: null },
          headers: { 'Idempotency-Key': actionKey.current() },
        }),
      ),
    onSettled: (_data, error) => actionKey.settle(error ?? undefined),
    onSuccess: () => void query.refetch(),
  });

  const close = useMutation({
    mutationFn: async (allowDebt: boolean) => {
      let approvalId: string | null = null;
      if (allowDebt && visit) {
        const balance = visit.invoices.reduce((sum, invoice) => sum + invoice.balance.amount, 0);
        const outcome = await requireApproval({ key: 'approval.debt.max_amount', amount: balance, context: { visitId: visit.id } });
        if (outcome.status === 'cancelled' || outcome.status === 'requested') {
          return null;
        }
        approvalId = outcome.status === 'approved' ? outcome.approvalId : null;
      }
      return unwrap(
        tenantApi.POST('/api/v1/billing/visits/{id}:close', {
          params: { path: { id } },
          body: { allowDebt, approvalId },
          headers: { 'Idempotency-Key': actionKey.current() },
        }),
      );
    },
    onSettled: (_data, error) => actionKey.settle(error ?? undefined),
    onSuccess: (view) => {
      if (view) {
        update(view);
        setCloseOpen(false);
        setToast(t('bil:close.done'));
      }
    },
  });

  const reopen = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/billing/visits/{id}:reopen', {
          params: { path: { id } },
          body: { reason },
          headers: { 'Idempotency-Key': actionKey.current() },
        }),
      ),
    onSettled: (_data, error) => actionKey.settle(error ?? undefined),
    onSuccess: (view) => {
      update(view);
      setReopenOpen(false);
    },
  });

  // میانبرهای صفحه (IA §5، FLOW-04): Ins افزودن قلم؛ Alt+P «دریافت وجه» (C-AGENT-549): صدور اقلام صادرنشده و سپس صندوق؛ بدون
  // bil.payment.receive فقط صدور.
  const shortcuts = useRef({ add: () => {}, issue: () => {} });
  shortcuts.current = {
    add: () => can.manage && setAdding(true),
    issue: () => {
      if (issue.isPending) {
        return;
      }
      if (can.issue) {
        toCashier.current = can.receive;
        issue.mutate();
      } else if (can.receive) {
        void navigate(checkoutPath(id));
      }
    },
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (event.key === 'Insert' && !typing) {
        event.preventDefault();
        shortcuts.current.add();
      } else if (event.altKey && (event.code === 'KeyP' || event.key === 'p' || event.key === 'P')) {
        event.preventDefault();
        shortcuts.current.issue();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (query.isPending) {
    return <LoadingState rows={6} />;
  }
  if (query.error || !visit) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const blockedLines = visit.lines.filter((line) => line.status === 'Rendered' && !line.invoiceId && line.pricingStatus === 'RequiresAmount');
  const balance = visit.invoices.reduce((sum, invoice) => sum + invoice.balance.amount, 0);
  const closeError = close.error;
  const needsDebt = isApiError(closeError) && closeError.code === 'PHX-BIL-0107' && closeError.extensions.reason === 'balance';
  const error = issue.error ?? lineAction.error ?? removeDiscount.error ?? applySuggested.error ?? resolve.error;
  const toggle = (lineId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  const COLS = 10;

  return (
    <Stack data-screen-id={SCR_STAFF_400} spacing={2}>
      <PageHeader
        titleKey="bil:visit.title"
        badges={
          <Stack direction="row" spacing={1} alignItems="center">
            <VisitStatusChip status={visit.status} />
            {visit.branchName ? <Chip size="small" variant="outlined" label={visit.branchName} /> : null}
            <Typography variant="body2" color="text.secondary">
              <JalaliDate value={visit.openedAt} withTime />
            </Typography>
          </Stack>
        }
        actions={
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {can.issue && (
              <Button variant="contained" onClick={() => issue.mutate()} disabled={issue.isPending} data-testid="bil-issue">
                {t('bil:issue.action')}
                {can.receive ? '' : ' (Alt+P)'}
              </Button>
            )}
            {can.receive && (can.issue || balance > 0) && (
              <Button variant="contained" color="secondary" onClick={() => shortcuts.current.issue()} disabled={issue.isPending} data-testid="bil-receive">
                {t('bil:cashier.receive')} (Alt+P)
              </Button>
            )}
            {can.close && (
              <Button variant="outlined" onClick={() => setCloseOpen(true)} data-testid="bil-close">
                {t('bil:close.action')}
              </Button>
            )}
            {can.reopen && (
              <Button variant="outlined" color="warning" onClick={() => setReopenOpen(true)} data-testid="bil-reopen">
                {t('bil:reopen.action')}
              </Button>
            )}
          </Stack>
        }
      />
      {access.can('pat.patient.view') && <PatientHeader patientId={visit.patientId} compact />}
      {error ? <ErrorState error={error} compact /> : null}
      {visit.status === 'Reopened' && (
        <Alert severity="warning" data-testid="bil-reopened">
          {t('bil:reopen.banner', { reason: visit.reopenReason ?? '' })}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              {t('bil:payer.title')}
            </Typography>
            <Typography data-testid="bil-payer">{payerLabel(visit.payer)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              {t('bil:payer.contract')}
            </Typography>
            <Typography>—</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" component="div">
              {t('bil:payer.insurance')}
            </Typography>
            <Typography>{t('bil:payer.insurance_phase3')}</Typography>
          </Box>
          {can.manage && (
            <Button size="small" onClick={() => setPayerOpen(true)} data-testid="bil-payer-change">
              {t('bil:payer.change')}
            </Button>
          )}
          {can.manage && visit.suggestedPayer && visit.suggestedPayer.kind !== visit.payer.kind && (
            <Button size="small" variant="outlined" onClick={() => applySuggested.mutate(visit)} data-testid="bil-payer-suggested">
              {t('bil:payer.apply_suggested', { name: payerLabel(visit.suggestedPayer) })}
            </Button>
          )}
        </Stack>
      </Paper>

      {blockedLines.length > 0 && (
        <Alert severity="warning" data-testid="bil-requires-amount">
          {t('bil:lines.requires_amount_alert', { count: blockedLines.length })}
        </Alert>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" aria-label={t('bil:lines.caption')}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{t('bil:lines.service')}</TableCell>
                <TableCell>{t('bil:lines.quantity')}</TableCell>
                <TableCell>{t('bil:lines.provider')}</TableCell>
                <TableCell>{t('bil:lines.price')}</TableCell>
                <TableCell>{t('bil:lines.discount')}</TableCell>
                <TableCell>{t('bil:lines.package')}</TableCell>
                <TableCell>{t('bil:lines.payable')}</TableCell>
                <TableCell>{t('bil:lines.status')}</TableCell>
                <TableCell>{t('bil:lines.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visit.lines.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLS}>
                    <Typography color="text.secondary">{t('bil:lines.empty')}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {visit.lines.map((line) => {
                const open = expanded.has(line.id);
                const discount = line.automaticDiscount.amount + line.manualDiscountAmount.amount;
                const editable = line.editable && can.manage;
                return (
                  <Fragment key={line.id}>
                    <TableRow data-testid="bil-line" data-line-id={line.id} data-status={line.status} data-pricing={line.pricingStatus} hover>
                      <TableCell padding="checkbox">
                        <IconButton size="small" aria-label={t('bil:layers.toggle')} aria-expanded={open} onClick={() => toggle(line.id)} data-testid="bil-line-expand">
                          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <span>{line.title}</span>
                          <Typography variant="caption" color="text.secondary">
                            {t(`bil:source.${line.source}`)}
                            {line.invoiceId ? ` · ${t('bil:lines.invoiced')}` : ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{faDigits(num(line.quantity))}</TableCell>
                      <TableCell>{line.providerName ?? '—'}</TableCell>
                      <TableCell>
                        {line.pricingStatus === 'RequiresAmount' ? (
                          <Chip size="small" color="warning" label={t('bil:lines.requires_amount')} data-testid="bil-line-requires-amount" />
                        ) : (
                          <Amount money={line.gross} />
                        )}
                      </TableCell>
                      <TableCell>{discount > 0 ? <Amount money={{ amount: discount, currency: line.gross.currency }} /> : '—'}</TableCell>
                      <TableCell>
                        <LinePackageCell visit={visit} line={line} canManage={can.manage} />
                      </TableCell>
                      <TableCell>
                        <Amount money={line.patientPayable} testId="bil-line-payable" />
                      </TableCell>
                      <TableCell>
                        <LineStatusChip status={line.status} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                          {editable && line.status !== 'Cancelled' && (
                            <Button size="small" onClick={() => setLineDialog({ kind: 'edit', line })} data-testid="bil-line-edit">
                              {t('bil:lines.edit')}
                            </Button>
                          )}
                          {editable && can.amount && line.rangeMin && line.status !== 'Cancelled' && (
                            <Button size="small" onClick={() => setLineDialog({ kind: 'amount', line })} data-testid="bil-line-amount">
                              {t('bil:amount.action')}
                            </Button>
                          )}
                          {line.editable && can.discount && line.status !== 'Cancelled' && (
                            <Button size="small" onClick={() => setLineDialog({ kind: 'discount', line })} data-testid="bil-line-discount">
                              {t('bil:discount.action')}
                            </Button>
                          )}
                          {line.editable && can.discount && line.manualDiscount && (
                            <Button size="small" color="inherit" onClick={() => removeDiscount.mutate(line)} data-testid="bil-line-discount-remove">
                              {t('bil:discount.remove')}
                            </Button>
                          )}
                          {editable && line.status === 'Planned' && (
                            <Button size="small" onClick={() => lineAction.mutate({ line, action: 'render' })} data-testid="bil-line-render">
                              {t('bil:lines.render')}
                            </Button>
                          )}
                          {editable && line.status !== 'Cancelled' && (
                            <Button size="small" color="error" onClick={() => setLineDialog({ kind: 'cancel', line })} data-testid="bil-line-cancel">
                              {t('bil:lines.cancel')}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {open && <LayersRow line={line} colSpan={COLS} />}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack direction="row" spacing={1} sx={{ p: 1 }}>
          {can.manage && (
            <Button onClick={() => setAdding(true)} data-testid="bil-line-add">
              + {t('bil:lines.add')} (Ins)
            </Button>
          )}
          {can.discount && (
            <Button onClick={() => setInvoiceDiscount(true)} data-testid="bil-invoice-discount">
              {t('bil:discount.invoice_action')}
            </Button>
          )}
          {can.manage && (
            <Button onClick={() => setDiscountInputs(true)} data-testid="bil-visit-discount-inputs">
              {t('bil:discount_inputs.action')}
              {(visit.discountInputs?.codes.length ?? 0) + (visit.discountInputs?.ruleIds.length ?? 0) > 0
                ? ` (${faDigits((visit.discountInputs?.codes.length ?? 0) + (visit.discountInputs?.ruleIds.length ?? 0))})`
                : ''}
            </Button>
          )}
          {can.discount && visit.invoiceDiscount && (
            <Button color="inherit" onClick={() => removeDiscount.mutate(null)} data-testid="bil-invoice-discount-remove">
              {t('bil:discount.remove')}
            </Button>
          )}
        </Stack>
      </Paper>

      <TotalsPanel totals={visit.totals} />

      {visit.invoices.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('bil:invoices.title')}
          </Typography>
          <Table size="small" aria-label={t('bil:invoices.title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('bil:invoices.columns.number')}</TableCell>
                <TableCell>{t('bil:invoices.columns.type')}</TableCell>
                <TableCell>{t('bil:invoices.columns.payer')}</TableCell>
                <TableCell>{t('bil:invoices.columns.payable')}</TableCell>
                <TableCell>{t('bil:invoices.columns.balance')}</TableCell>
                <TableCell>{t('bil:invoices.columns.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visit.invoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid="bil-visit-invoice">
                  <TableCell>
                    <Link component={RouterLink} to={invoicePath(invoice.id)} data-testid="bil-invoice-link">
                      <bdi dir="ltr">{invoice.number}</bdi>
                    </Link>
                  </TableCell>
                  <TableCell>{t(`bil:invoice_type.${invoice.type}`)}</TableCell>
                  <TableCell>{payerLabel(invoice.payer)}</TableCell>
                  <TableCell>
                    <Amount money={invoice.payable} />
                  </TableCell>
                  <TableCell>
                    <Amount money={invoice.balance} />
                  </TableCell>
                  <TableCell>
                    <InvoiceStatusChip status={invoice.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {visit.corrections.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="bil-corrections">
          <Typography variant="subtitle1" gutterBottom>
            {t('bil:corrections.title')}
          </Typography>
          <Stack spacing={1}>
            {visit.corrections.map((correction) => (
              <Stack key={correction.id} direction="row" spacing={2} alignItems="center" data-testid="bil-correction" data-status={correction.status}>
                <Typography variant="body2">
                  {t(`bil:corrections.kind.${correction.changeKind}`, { defaultValue: correction.changeKind })}
                  {correction.oldQuantity !== null || correction.newQuantity !== null
                    ? ` · ${faDigits(num(correction.oldQuantity))} ← ${faDigits(num(correction.newQuantity))}`
                    : ''}
                </Typography>
                <JalaliDate value={correction.detectedAt} withTime />
                <Chip size="small" label={t(`bil:corrections.status.${correction.status}`, { defaultValue: correction.status })} />
                {correction.status === 'Open' && full && access.can('bil.visit.manage') && (
                  <Button size="small" onClick={() => resolve.mutate(correction.id)} data-testid="bil-correction-resolve">
                    {t('bil:corrections.resolve')}
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      {adding && <AddLineDialog visit={visit} onClose={() => setAdding(false)} />}
      {lineDialog?.kind === 'edit' && <EditLineDialog visit={visit} line={lineDialog.line} onClose={() => setLineDialog(null)} />}
      {lineDialog?.kind === 'amount' && <AmountDialog visit={visit} line={lineDialog.line} onClose={() => setLineDialog(null)} />}
      {lineDialog?.kind === 'discount' && <DiscountDialog visit={visit} line={lineDialog.line} onClose={() => setLineDialog(null)} />}
      {invoiceDiscount && <DiscountDialog visit={visit} line={null} onClose={() => setInvoiceDiscount(false)} />}
      {discountInputs && <DiscountInputsDialog visit={visit} onClose={() => setDiscountInputs(false)} />}
      {payerOpen && <PayerDialog visit={visit} onClose={() => setPayerOpen(false)} />}
      <ReasonDialog
        open={lineDialog?.kind === 'cancel'}
        titleKey="bil:lines.cancel_title"
        busy={lineAction.isPending}
        error={lineAction.error}
        onConfirm={(reason) => lineDialog && lineAction.mutate({ line: lineDialog.line, action: 'cancel', reason })}
        onCancel={() => setLineDialog(null)}
      />
      <ConfirmDialog
        open={closeOpen}
        titleKey="bil:close.title"
        messageKey={balance > 0 ? 'bil:close.message_balance' : 'bil:close.message'}
        confirmKey={needsDebt && access.can('bil.debt.allow') ? 'bil:close.with_debt' : 'bil:close.action'}
        busy={close.isPending}
        error={closeError}
        onConfirm={() => close.mutate(needsDebt && access.can('bil.debt.allow'))}
        onCancel={() => {
          setCloseOpen(false);
          close.reset();
        }}
      />
      <ReasonDialog
        open={reopenOpen}
        titleKey="bil:reopen.title"
        messageKey="bil:reopen.message"
        confirmKey="bil:reopen.action"
        destructive={false}
        busy={reopen.isPending}
        error={reopen.error}
        onConfirm={(reason) => reopen.mutate(reason)}
        onCancel={() => setReopenOpen(false)}
      />
      {approvalDialog}
      <Snackbar open={toast !== null} autoHideDuration={5000} onClose={() => setToast(null)} message={toast ?? ''} />
    </Stack>
  );
}
